import React, { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound, Mail, ShieldCheck } from "lucide-react";

import api from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const emptyForm = {
  current_password: "",
  new_password: "",
  new_password_confirmation: "",
};

// A password input with a show/hide eye toggle. Defined at module scope so it
// keeps a stable identity across renders (otherwise the input loses focus on
// every keystroke).
const PasswordField = ({ id, label, value, onChange, error, visible, onToggle }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <div className="relative">
      <Input
        id={id}
        type={visible ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="••••••••"
        className={`pr-10 ${error ? "border-red-400" : ""}`}
      />
      <button
        type="button"
        onClick={onToggle}
        title={visible ? "Hide password" : "Show password"}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

const ChangePasswordDialog = ({ open, onOpenChange, user, onSuccess }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  const reset = () => {
    setForm(emptyForm);
    setErrors({});
    setShow({ current: false, next: false, confirm: false });
  };

  // Validate one field; mirrors the Laravel changePassword rules.
  const validateField = (name, value, state = form) => {
    const v = value || "";
    switch (name) {
      case "current_password":
        if (!v) return "Current password is required.";
        return "";
      case "new_password":
        if (!v) return "New password is required.";
        if (v.length < 8) return "Password must be at least 8 characters.";
        if (v === state.current_password)
          return "New password must be different from the current one.";
        return "";
      case "new_password_confirmation":
        if (!v) return "Please confirm the new password.";
        if (v !== state.new_password) return "Passwords do not match.";
        return "";
      default:
        return "";
    }
  };

  const validateAll = () => {
    const next = {};
    Object.keys(emptyForm).forEach((key) => {
      const err = validateField(key, form[key], form);
      if (err) next[key] = err;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const setField = (key) => (value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    setErrors((prev) => {
      const e = { ...prev, [key]: validateField(key, value, next) };
      // Keep dependent fields in sync.
      if (key === "new_password") {
        e.new_password_confirmation = validateField(
          "new_password_confirmation",
          next.new_password_confirmation,
          next
        );
      }
      if (key === "current_password") {
        e.new_password = validateField("new_password", next.new_password, next);
      }
      return e;
    });
  };

  const handleClose = (nextOpen) => {
    if (saving) return;
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) {
      toast.error(
        "We identified an issue with the information you provided. Please review the form again."
      );
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post("/change-password", form);
      toast.success(data?.message || "Password changed successfully");
      reset();
      onOpenChange(false);
      // Let the parent know so it can clear the "default password" flag.
      onSuccess?.();
    } catch (error) {
      const result = error.response?.data;
      // 422 "Current password is incorrect" comes back as a message (no errors).
      if (result?.errors) {
        const firstError = Object.values(result.errors)?.[0]?.[0];
        toast.error(firstError || "Failed to change password");
      } else if (result?.message) {
        // Surface a wrong current password under that field too.
        if (/current password/i.test(result.message)) {
          setErrors((prev) => ({ ...prev, current_password: result.message }));
        }
        toast.error(result.message);
      } else {
        toast.error("Failed to change password");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => () =>
    setShow((s) => ({ ...s, [key]: !s[key] }));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>My Account</DialogTitle>
          <DialogDescription>
            Your account details. Update your password below if needed.
          </DialogDescription>
        </DialogHeader>

        {/* User info */}
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#b32126]/10 text-lg font-semibold text-[#b32126]">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-gray-900">
              {user?.name || "—"}
            </p>
            {user?.email && (
              <p className="flex items-center gap-1.5 truncate text-sm text-gray-500">
                <Mail className="h-3.5 w-3.5 shrink-0" />
                {user.email}
              </p>
            )}
            {user?.role && (
              <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#b32126]/10 px-2 py-0.5 text-xs font-medium text-[#b32126]">
                <ShieldCheck className="h-3 w-3" />
                {user.role}
              </span>
            )}
          </div>
        </div>

        {/* Change password */}
        <div className="flex items-center gap-2 border-t border-gray-100 pt-4 text-sm font-semibold text-gray-900">
          <KeyRound className="h-4 w-4 text-[#b32126]" />
          Change Password
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <PasswordField
            id="current-password"
            label="Current Password"
            value={form.current_password}
            onChange={setField("current_password")}
            error={errors.current_password}
            visible={show.current}
            onToggle={toggle("current")}
          />
          <PasswordField
            id="new-password"
            label="New Password"
            value={form.new_password}
            onChange={setField("new_password")}
            error={errors.new_password}
            visible={show.next}
            onToggle={toggle("next")}
          />
          <PasswordField
            id="confirm-password"
            label="Confirm New Password"
            value={form.new_password_confirmation}
            onChange={setField("new_password_confirmation")}
            error={errors.new_password_confirmation}
            visible={show.confirm}
            onToggle={toggle("confirm")}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#b32126] hover:bg-[#8f1a1f]"
            >
              {saving ? "Saving…" : "Change Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
